import { Box } from '@spark-web/box';
import { Divider } from '@spark-web/divider';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { TextLinkButton } from '@spark-web/text-link';
import { useState } from 'react';

import { RatingResponse } from '~/gql/graphql';

import googleLogo from '../../../public/google-logo.svg';

type VendorRatingProps = {
  rating: RatingResponse | null;
};

const MAX_STARS = 5;

export const VendorRating = ({ rating }: VendorRatingProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Box display="flex" flexDirection="row" gap="small" alignItems="center">
      <img src={googleLogo} alt="Google logo" height="15" width="15" />
      <StarRating name={rating?.name as string} rating={rating?.rating as number} />
      <Text size="xsmall">{rating?.rating}</Text>
      <Text>·</Text>
      <Text tone="info" size="xsmall">
        <TextLinkButton onClick={() => setIsOpen(true)}>{rating?.reviewTotal} reviews</TextLinkButton>
      </Text>
      <ContentDialog
        title={`Reviews for ${rating?.name}`}
        isOpen={isOpen}
        onToggle={() => setIsOpen(false)}
        onCloseAutoFocus={() => setIsOpen(false)}
      >
        {rating?.reviews?.map((review) => (
          <Stack key={review?.authorName} gap="medium">
            <Text weight="semibold">{review?.authorName}</Text>
            <Box display="flex" flexDirection="row" gap="small" alignItems="center">
              <StarRating name={rating?.name as string} rating={review?.rating as number} />
              <Text size="xsmall">{review?.relativeTimeDescription}</Text>
            </Box>
            <Text size="small">{review?.text}</Text>
            <Divider />
          </Stack>
        ))}
      </ContentDialog>
    </Box>
  );
};

const Star = ({ filled = false, fraction = 0 }: { filled?: boolean; fraction?: number }) => {
  const starPath = 'M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z';

  const fillColor = filled ? '#FFCC00' : '#C7CEDB';

  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20">
      <path d={starPath} fill={fillColor} />

      {fraction > 0 && !filled && (
        <path
          d={starPath}
          fill="#FFCC00"
          style={{
            clipPath: `inset(0 ${100 - fraction * 100}% 0 0)`,
          }}
        />
      )}
    </svg>
  );
};

const StarRating = ({ name, rating }: { name: string; rating: number }) => {
  const fullStars = Math.floor(rating);
  const fractionedStars = rating - fullStars;
  const emptyStars = MAX_STARS - Math.ceil(rating);

  const renderStars = () => {
    const stars = [];

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`${name}-filled-${i}`} filled />);
    }

    if (fractionedStars > 0) {
      stars.push(<Star key={`${name}-partial`} fraction={fractionedStars} />);
    }

    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`${name}-empty-${i}`} />);
    }

    return stars;
  };

  return <Box display="flex">{renderStars()}</Box>;
};
